interface DeferredIdleDeadline {
  didTimeout: boolean
  timeRemaining: () => number
}

type DeferredRenderWindow = Pick<Window,
| 'requestAnimationFrame'
| 'cancelAnimationFrame'
| 'setTimeout'
| 'clearTimeout'> & {
  requestIdleCallback?: (callback: (deadline: DeferredIdleDeadline) => void, options: { timeout: number }) => number
  cancelIdleCallback?: (handle: number) => void
  IntersectionObserver?: typeof IntersectionObserver
};

interface DeferredJob {
  order: number
  sequence: number
  element: Element
  mount: () => void
  visible: boolean
}

/** One coordinator belongs to one mounted schema, never to the module. */
export class DeferredRenderScheduler {
  private readonly queue = new Map<symbol, DeferredJob>();
  private readonly elements = new Map<Element, symbol>();
  private observer?: IntersectionObserver;
  private pending?: { kind: 'visible' | 'background', cancel: () => void };
  private generation = 0;
  private sequence = 0;
  private paused = false;
  private disposed = false;
  private backgroundDeadline?: number;

  public constructor (
    private readonly browser: DeferredRenderWindow = window,
    private readonly now: () => number = Date.now,
  ) {}

  public register = (token: symbol, order: number, element: Element, mount: () => void): void => {
    if (this.disposed) { return; }
    this.unregister(token);
    this.queue.set(token, { order, element, mount, visible: false, sequence: this.sequence++ });
    this.elements.set(element, token);
    this.observe(element);
    this.ensureScheduled();
  };

  public updateOrder = (token: symbol, order: number): void => {
    const job = this.queue.get(token);
    if (job) { job.order = order; }
  };

  public unregister = (token: symbol): void => {
    const job = this.queue.get(token);
    if (!job) { return; }
    this.queue.delete(token);
    this.elements.delete(job.element);
    this.observer?.unobserve(job.element);
    if (this.queue.size === 0) {
      this.cancelPending();
      this.observer?.disconnect();
      this.observer = undefined;
      this.backgroundDeadline = undefined;
    } else if (this.pending) {
      this.ensureScheduled();
    }
  };

  public setPaused = (paused: boolean): void => {
    this.paused = paused;
    if (paused) {
      this.cancelPending();
    } else {
      this.ensureScheduled();
    }
  };

  public dispose = (): void => {
    this.disposed = true;
    this.cancelPending();
    this.observer?.disconnect();
    this.observer = undefined;
    this.elements.clear();
    this.queue.clear();
  };

  private observe (element: Element): void {
    const Observer = this.browser.IntersectionObserver;
    if (!Observer) { return; }
    if (!this.observer) {
      this.observer = new Observer((entries) => {
        entries.forEach((entry) => {
          const token = this.elements.get(entry.target);
          const job = token && this.queue.get(token);
          if (job) { job.visible = entry.isIntersecting; }
        });
        // Visibility changes priority, never mounts a burst of children.
        this.ensureScheduled();
      }, { rootMargin: '400px' });
    }
    this.observer.observe(element);
  }

  private cancelPending (): void {
    this.generation++;
    this.pending?.cancel();
    this.pending = undefined;
  }

  private ensureScheduled (): void {
    if (this.disposed || this.paused || this.queue.size === 0) { return; }
    const kind = Array.from(this.queue.values()).some((job) => job.visible) ? 'visible' : 'background';
    if (this.pending?.kind === kind) { return; }
    this.cancelPending();
    const generation = this.generation;
    const run = (deadline?: DeferredIdleDeadline) => {
      if (generation !== this.generation || this.disposed || this.paused) { return; }
      this.pending = undefined;
      if (deadline && !deadline.didTimeout && deadline.timeRemaining() < 5
        && this.now() < (this.backgroundDeadline ?? 0)) {
        this.ensureScheduled();
        return;
      }
      this.admitOne();
      this.ensureScheduled();
    };

    if (kind === 'visible') {
      const handle = this.browser.requestAnimationFrame(() => run());
      this.pending = { kind, cancel: () => this.browser.cancelAnimationFrame(handle) };
    } else if (this.browser.requestIdleCallback) {
      const now = this.now();
      // Keep one deadline across short idle slices so they cannot postpone
      // admission indefinitely by restarting the timeout every time.
      this.backgroundDeadline = this.backgroundDeadline ?? now + 300;
      const handle = this.browser.requestIdleCallback(run, { timeout: Math.max(0, this.backgroundDeadline - now) });
      this.pending = { kind, cancel: () => this.browser.cancelIdleCallback?.(handle) };
    } else {
      const handle = this.browser.setTimeout(() => run(), 50);
      this.pending = { kind, cancel: () => this.browser.clearTimeout(handle) };
    }
  }

  private admitOne (): void {
    const entry = Array.from(this.queue.entries()).sort(([, left], [, right]) =>
      Number(right.visible) - Number(left.visible)
      || left.order - right.order
      || left.sequence - right.sequence,
    )[0];
    if (!entry) { return; }
    const [token, job] = entry;
    this.backgroundDeadline = undefined;
    this.unregister(token);
    job.mount();
  }
}
